import { v4 as uuid } from 'uuid';

import type * as types from '../types';

/**
 * Prunes the provided {@link types.LogContext} by removing keys that are associated with explicitly
 * undefined values.
 *
 * @param {types.LogContext} ctx The context that should be pruned.
 *
 * @returns {types.LogContext | undefined}
 *   The originally provided context with keys that have undefined values removed.
 */
export const pruneContext = (ctx?: types.LogContext): types.LogContext | undefined => {
  if (ctx === undefined) {
    return undefined;
  }
  const pruned = Object.keys(ctx).reduce(
    (prev, key) => (ctx[key] === undefined ? prev : { ...prev, [key]: ctx[key] }),
    {} as types.LogContext,
  );
  if (Object.keys(pruned).length !== 0) {
    return pruned;
  }
  return undefined;
};

type ContextIterable = Error | types.LogContext | undefined;

/**
 * Converts the provided {@link Error} instance to a {@link types.LogContext} object by capturing
 * its enumerable own properties (such as a custom error's `code`).
 *
 * The error's non-enumerable `name`, `message`, and `stack` are intentionally NOT flattened into
 * the context. They are serialized into the dedicated `error` object by the {@link PinoStrategy}
 * (through pino's standard error serializer), which is the canonical, Datadog-friendly
 * representation of the error in the emitted logs. Flattening them here as well would duplicate
 * the error across two shapes in every log line.
 *
 * @param {Error} error The error that should be converted to a context, {@link types.LogContext}.
 *
 * @returns {types.LogContext | undefined}
 *   The representation of the provided {@link Error} as log context (a record-type),
 *   {@link types.LogContext}.
 */
export const convertErrorToContext = (error: Error): types.LogContext | undefined => {
  const errorContext: types.LogContext = {};
  for (const propertyName in error) {
    if (error.hasOwnProperty(propertyName)) {
      const propertyValue = error[propertyName as keyof typeof error];
      errorContext[propertyName] = propertyValue;
    }
  }
  return pruneContext(errorContext);
};

/**
 * Merges together the provided contexts, {@link types.LogContext}, ensuring that the keys in
 * context defined after do not overwrite keys defined in context before.  If a key is found to
 * already exist in the context, the key is suffixed with a unique identifier to ensure that the
 * key is unique in the context.
 *
 * This method is used to prevent the loss of data when merging contexts together. It ensures that
 * the keys in the original context do not get overwritten by the keys in the additional context.
 *
 * @returns {types.LogContext | undefined}
 *   The pruned, merged context.
 */
export const mergeContext = (original: ContextIterable, ...additional: ContextIterable[]) => {
  if (additional.length === 0) {
    return original instanceof Error ? convertErrorToContext(original) : pruneContext(original);
  }

  const merge = (a: ContextIterable, b: ContextIterable) => {
    let ctx = { ...a };
    const record = b instanceof Error ? convertErrorToContext(b) : b;

    for (const [key, value] of Object.entries(record ?? {})) {
      if (Object.keys(ctx).includes(key)) {
        ctx = { ...ctx, [`${key}_${uuid()}`]: value };
      } else {
        ctx = { ...ctx, [key]: value };
      }
    }
    return pruneContext(ctx);
  };

  let runningContext: types.LogContext = { ...original };
  for (const ctx of additional) {
    runningContext = { ...merge(runningContext, ctx) };
  }
  return pruneContext(runningContext);
};
